import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { applyTheme, getInitialTheme, setStoredTheme, type Theme } from "../lib/theme";

export default function ThemeToggle({ compact = false }: { compact?: boolean }) {
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme());

  // Sync on mount (in case initial theme was determined pre-render but not applied)
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  function toggle() {
    const next: Theme = theme === "dark" ? "light" : "dark";
    setTheme(next);
    setStoredTheme(next);
  }

  const isDark = theme === "dark";

  if (compact) {
    return (
      <button
        type="button"
        onClick={toggle}
        title={isDark ? "Switch to light" : "Switch to dark"}
        aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
        className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-glass text-parchment-muted hover:text-parchment hover:bg-surface-2 transition"
        style={{
          backgroundColor: "rgb(var(--surface-1))",
          borderColor: "rgb(var(--border-soft))",
        }}
      >
        {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      className="btn-ghost w-full justify-between"
      title={isDark ? "Switch to light" : "Switch to dark"}
    >
      <span className="flex items-center gap-2">
        {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        {isDark ? "Light mode" : "Dark mode"}
      </span>
      <span className="text-[10px] uppercase tracking-widest text-parchment-muted">
        {isDark ? "Dark" : "Light"}
      </span>
    </button>
  );
}
